import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "./apiSlice";
import { buildQueryString } from "../utils/buildQueryString";

// Create an adapter for the cancellations list
const cancellationsListAdapter = createEntityAdapter({
  selectId: (cancellation) => cancellation.id,
});

const initialState = cancellationsListAdapter.getInitialState();

// Define the slice
export const cancellationsListSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ✅ Fetch list of cancellations
    getCancellationsList: builder.query({
      query: (filters) =>
        buildQueryString("orders/cancellation_requests", filters),
      transformResponse: (responseData) => {
        const loadedCancellationsList = responseData.data.map((item) => ({
          ...item,
        }));

        return cancellationsListAdapter.setAll(
          initialState,
          loadedCancellationsList
        );
      },
      providesTags: (result) =>
        result
          ? [
              { type: "cancellations", id: "cancellationsList" },
              ...result.ids.map((id) => ({ type: "cancellations", id })),
            ]
          : [{ type: "cancellations", id: "cancellationsList" }],
    }),

    // ✅ Fetch individual cancellation details
    getCancellationById: builder.query({
      query: (cancellationId) => `cancellations/${cancellationId}`,
      transformResponse: (responseData) => responseData.data,
      providesTags: (result, error, cancellationId) => [
        { type: "cancellations", id: cancellationId },
      ],
    }),

    patchCancellationStatus: builder.mutation({
      query: ({ id, order_status, remarks }) => ({
        url: `orders/cancellation_requests`,
        method: "PATCH",
        body: { order_id: id, order_status, ...(remarks && { remarks }) },
      }),
      // transformResponse: (response) => {
      //   console.log("Cancellation PATCH response:", response);
      //   return response?.data;
      // },
      onQueryStarted: async (
        { id, order_status, remarks, filters },
        { dispatch, queryFulfilled }
      ) => {
        try {
          const { data: updatedCancellation } = await queryFulfilled;

          dispatch(
            apiSlice.util.updateQueryData(
              "getCancellationsList",
              filters || {},
              (draft) => {
                const existing = draft.entities[id];
                if (existing) {
                  Object.assign(existing, updatedCancellation);
                }
              }
            )
          );
        } catch (err) {
          console.error("Failed to update cancellation:", err);
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "cancellations", id },
        { type: "cancellations", id: "cancellationsList" },
        { type: "ProfilePaymentSummary" },
      ],
    }),
  }),
});

// Export hooks for API calls
export const {
  useGetCancellationsListQuery,
  useGetCancellationByIdQuery,
  usePatchCancellationStatusMutation,
} = cancellationsListSlice;

// Select the cancellation filter state
const cancellationFilter = (state) => state.cancellationFilter;

// ✅ Select filtered cancellations from the state
const selectCancellationListResult = createSelector(
  [cancellationFilter, (state) => state],
  (filter, state) =>
    cancellationsListSlice.endpoints.getCancellationsList.select({
      status: filter.status,
      search: filter.search,
    })(state)
);

const selectCancellationsListData = createSelector(
  [selectCancellationListResult],
  (cancellationListResult) => cancellationListResult?.data ?? initialState
);

// ✅ Adapter selectors for cancellations
export const {
  selectAll: selectCancellations,
  selectById: selectCancellationById,
  selectIds: selectCancellationIds,
} = cancellationsListAdapter.getSelectors((state) =>
  selectCancellationsListData(state)
);
